import { useParams, Navigate } from "react-router-dom";
import { AlertCircle, Check, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import FAQ from "../components/FAQ";
import { Reveal, SectionLabel, Stagger, Item } from "../components/Reveal";
import { FinalCta } from "../components/CTA";
import { servicePages } from "../data/servicePages";
import { useContent, sortedPackages, pkgPrice, faqsFor } from "../data/siteContent";

const groupFor = { "brand-website": "brand", automation: "whatsapp", sops: "sops" };

export default function ServicePage() {
  const { slug } = useParams();
  const content = useContent();
  const page = servicePages.find((p) => p.slug === slug);
  if (!page) return <Navigate to="/" replace />;

  const packages = sortedPackages(content, groupFor[page.slug]);
  const faqs = faqsFor(content, page.slug);
  const faqSchema = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div data-testid={`service-page-${page.slug}`}>
      <SEO title={page.seoTitle} description={page.line} path={`/services/${page.slug}`} jsonLd={faqSchema} />
      <PageHero eyebrow="Allcov Services" title={page.title} line={page.line} withCtas testid={`service-${page.slug}`} />

      {/* PROBLEMS */}
      <section className="py-20 lg:py-28" data-testid="service-problems">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><SectionLabel>{page.problemsTitle}</SectionLabel></Reveal>
          <Stagger className="grid gap-4 md:grid-cols-2">
            {page.problems.map((p) => (
              <Item key={p}>
                <div className="flex h-full items-start gap-4 border border-line bg-surface p-6">
                  <AlertCircle size={20} className="mt-0.5 shrink-0 text-pop" strokeWidth={1.5} />
                  <p className="text-sm leading-relaxed text-mut md:text-base">{p}</p>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="section-light py-20 lg:py-28" data-testid="service-get">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><SectionLabel>{page.getTitle}</SectionLabel></Reveal>
          <Stagger className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {page.getItems.map((g) => (
              <Item key={g}>
                <div className="flex h-full items-start gap-3 border border-line bg-surface p-5">
                  <Check size={18} className="mt-0.5 shrink-0 text-pop" />
                  <p className="text-sm leading-relaxed text-mut">{g}</p>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* PACKAGES */}
      {packages.length > 0 && (
        <section className="py-20 lg:py-28" data-testid="service-packages">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="flex flex-wrap items-baseline gap-4">
              <Reveal><SectionLabel>{page.packagesTitle}</SectionLabel></Reveal>
              <p className="text-xs uppercase tracking-[0.2em] text-faint">{content.gst_note}</p>
            </div>
            <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {packages.map((p) => (
                <Item key={p.id || p.name} className="h-full">
                  <div className="inner-glow flex h-full flex-col border border-line bg-surface p-7" data-testid={`package-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <p className="font-batangas text-xl tracking-tight">{p.name}</p>
                    <p className="mt-3 font-batangas text-2xl tracking-tight text-pop">{pkgPrice(p)}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-faint">{p.timeline}</p>
                    <ul className="mt-5 flex-1 space-y-2.5">
                      {p.inclusions.map((it) => (
                        <li key={it} className="flex items-start gap-2.5 text-sm text-mut">
                          <Check size={14} className="mt-0.5 shrink-0 text-pop" />
                          {it}
                        </li>
                      ))}
                    </ul>
                    {p.note && <p className="mt-4 border-t border-line pt-4 text-xs leading-relaxed text-faint">{p.note}</p>}
                  </div>
                </Item>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="section-light py-20 lg:py-28" data-testid="service-steps">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><SectionLabel>How It Works</SectionLabel></Reveal>
          <Stagger className="flex flex-wrap gap-3">
            {page.steps.map((s, i) => (
              <Item key={s}>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-3 border border-line bg-surface px-5 py-3.5 text-sm text-mut">
                    <span className="font-batangas text-lg text-pop">{i + 1}</span>
                    {s}
                  </span>
                  {i < page.steps.length - 1 && <ArrowRight size={16} className="hidden shrink-0 text-faint md:block" aria-hidden="true" />}
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-20 lg:py-28" data-testid="service-faq">
          <div className="mx-auto max-w-4xl px-6 lg:px-10">
            <Reveal><SectionLabel>FAQ</SectionLabel></Reveal>
            <Reveal delay={0.1}>
              <FAQ items={faqs} testid={`faq-${page.slug}`} />
            </Reveal>
          </div>
        </section>
      )}

      <FinalCta />
    </div>
  );
}
